import type { ModelCost, ModelRef, ModelSpeed, ModelTask } from "../lib/types.js";

/** Friendly labels for the catalog's speed/cost classifications. */
export const SPEED_LABEL: Record<ModelSpeed, string> = {
  "very-fast": "Very fast",
  fast: "Fast",
  moderate: "Moderate",
  slow: "Slow"
};
export const COST_LABEL: Record<ModelCost, string> = {
  "very-low": "Very low cost",
  low: "Low cost",
  moderate: "Moderate cost",
  higher: "Higher cost"
};

// ---- Per-viewer remembered choices (localStorage, best-effort) ----
// Mirrors the app's existing `clovascribe:` convention; all access is wrapped in
// try/catch so private windows / blocked storage never break the UI.
const taskKey = (task: ModelTask, projectId: string) => `clovascribe:aiModel:${task}:${projectId}`;
const chatKey = (projectId: string) => `clovascribe:chatModel:${projectId}`;

function read(key: string): ModelRef | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as ModelRef;
    return v && typeof v.provider === "string" && typeof v.model === "string" ? v : null;
  } catch {
    return null;
  }
}
function write(key: string, ref: ModelRef): void {
  try {
    localStorage.setItem(key, JSON.stringify({ provider: ref.provider, model: ref.model }));
  } catch {
    /* ignore */
  }
}

export const getRememberedModel = (task: ModelTask, projectId: string) => read(taskKey(task, projectId));
export const rememberModel = (task: ModelTask, projectId: string, ref: ModelRef) => write(taskKey(task, projectId), ref);
export const getChatModel = (projectId: string) => read(chatKey(projectId));
export const setChatModel = (projectId: string, ref: ModelRef) => write(chatKey(projectId), ref);

/** Friendly labels for the per-action tasks that can have a remembered model. */
export const TASK_LABEL: Record<ModelTask, string> = {
  extraction: "Auto-populate from book",
  continuity: "Continuity analysis",
  critique: "Story critique",
  summary: "Summaries",
  rewrite: "Rewrite / continue",
  brainstorm: "Brainstorm",
  timeline: "Timeline",
  "character-analysis": "Relationship detection",
  "general-chat": "Chat"
};

const ALL_TASKS = Object.keys(TASK_LABEL) as ModelTask[];

/** The per-action model choices the user has chosen to remember for this project. */
export function listRememberedModels(projectId: string): { task: ModelTask; ref: ModelRef }[] {
  const out: { task: ModelTask; ref: ModelRef }[] = [];
  for (const task of ALL_TASKS) {
    const ref = read(taskKey(task, projectId));
    if (ref) out.push({ task, ref });
  }
  return out;
}

export function clearRememberedModel(task: ModelTask, projectId: string): void {
  try { localStorage.removeItem(taskKey(task, projectId)); } catch { /* ignore */ }
}
export function clearAllRememberedModels(projectId: string): void {
  for (const task of ALL_TASKS) clearRememberedModel(task, projectId);
}
